import {
  catchError,
  map,
  mapErrorEffectPassThrough,
  pipe,
  succeed,
  tryRunSync,
  unbox,
} from "@otto-ec/assets-core-utils/nano-effect";
import type { SetRequired } from "type-fest";
import {
  defineFilterCb,
  transactionTypes,
  type APMTransactionPayload,
  type SpanPayload,
} from "./utils";

type ResourceBlockingType = "sync" | "async" | "unknown";
type ResourceType = "html" | "image" | "css" | "script" | "other";
type ResourceDeliveryType = "cache" | "nocache";
export type PerformanceResourceTimingEx = PerformanceResourceTiming & {
  renderBlockingStatus?: "blocking" | "non-blocking";
  deliveryType?: "cache";
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const parseBundleName = (
  url: string,
): readonly [team: string, service: string, bundle: string] => {
  const res = pipe(
    tryRunSync(() => new URL(url), mapErrorEffectPassThrough<Error>),
    map(({ pathname }) => {
      const parts = pathname.split("/").at(-1)?.split(".");
      if (!parts || parts.length < 3) return ["unknown", "unknown", "unknown"] as const;
      const [team = "unknown", service = "unknown", bundle = "unknown"] = parts;
      return [team, service, bundle] as const;
    }),
    catchError(() => succeed(["unknown", "unknown", "unknown"] as const)),
    unbox((_) => _),
  );
  return res;
};

/**
 *
 *
 *
 *
 */
const getRenderBlockingStatus = (entry: PerformanceResourceTimingEx): ResourceBlockingType => {
  if ("renderBlockingStatus" in entry) {
    return entry.renderBlockingStatus === "blocking" ? "sync" : "async";
  }
  return "unknown";
};

const getDeliveryType = (entry: PerformanceResourceTimingEx): ResourceDeliveryType => {
  return entry.deliveryType ?? "nocache";
};

/**
 *
 *
 *
 *
 */
const getResourceType = (
  entry: PerformanceResourceTiming | PerformanceNavigationTiming,
): ResourceType => {
  if (entry.entryType === "navigation") return "html";
  if (entry.initiatorType === "img") return "image";
  if (entry.name.endsWith(".css")) return "css";
  if (entry.name.endsWith(".js")) return "script";
  return "other";
};

/*                                        */
const prefix = "resource";
const totalSuffix = "total";

/**
 *
 *
 */
type MetricLabels = Record<`${typeof prefix}_${string}_${string}`, number | undefined>;

/**
 *
 *
 *
 *
 */
const sumMetric = <const K extends keyof MetricLabels>(
  labels: MetricLabels,
  key: K,
  value: number,
): void => {
  labels[key] = (labels[key] ?? 0) + value;
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const aggregateResourceMetric = (
  labels: MetricLabels,
  resource: ResourceType,
  render: ResourceBlockingType,
  delivery: ResourceDeliveryType,
  metric: string,
  value: number,
): void => {
  const prefixMetric = `${prefix}_${metric}` as const;
  sumMetric(labels, `${prefixMetric}_${resource}_${render}_${delivery}`, value);
  sumMetric(labels, `${prefixMetric}_${resource}_${render}_${totalSuffix}`, value);
  sumMetric(labels, `${prefixMetric}_${resource}_${delivery}_${totalSuffix}`, value);
  sumMetric(labels, `${prefixMetric}_${resource}_${totalSuffix}`, value);
  sumMetric(labels, `${prefixMetric}_${render}_${totalSuffix}`, value);
  sumMetric(labels, `${prefixMetric}_${delivery}_${totalSuffix}`, value);
  sumMetric(labels, `${prefixMetric}_${totalSuffix}`, value);
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const aggregateResourceMetrics = defineFilterCb((payload) => {
  /*                            */
  if (!payload.transactions || !Array.isArray(payload.transactions)) return payload;
  const transactions = payload.transactions as APMTransactionPayload[];

  /*                                  */
  if (!transactions.length) return payload;

  /*                              */
  const pageload = transactions.find((tx) => tx.type === transactionTypes[0]);
  if (!pageload) return payload;

  const perfEntries = [
    ...performance.getEntriesByType("navigation"),
    ...performance.getEntriesByType("resource"),
  ] as (PerformanceNavigationTiming | PerformanceResourceTiming)[];

  const labels: MetricLabels = {};

  perfEntries.forEach((entry) => {
    const aggregate = aggregateResourceMetric.bind(
      null,
      labels,
      getResourceType(entry),
      getRenderBlockingStatus(entry),
      getDeliveryType(entry),
    );

    aggregate("transfer_size", entry.transferSize);
    aggregate("encoded_body_size", entry.encodedBodySize);
    aggregate("decoded_body_size", entry.decodedBodySize);
    aggregate("count", 1);
  });

  pageload.context ??= {};
  pageload.context.tags ??= {};
  Object.assign(pageload.context.tags, labels);

  return payload;
});

const validSpanSubtypes = ["script", "link", "img"];
const filterSpan = (
  span: SpanPayload,
): span is SetRequired<SpanPayload, "name" | "type" | "subtype"> =>
  !!span.name &&
  span.type === "resource" &&
  !!span.subtype &&
  validSpanSubtypes.includes(span.subtype);

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const enrichResourceSpans = defineFilterCb((payload) => {
  /*                            */
  if (!payload.transactions || !Array.isArray(payload.transactions)) return payload;
  const transactions = payload.transactions as APMTransactionPayload[];

  /*                                  */
  if (!transactions.length) return payload;

  transactions.forEach((tx) => {
    /*                 */
    if (!tx.spans || !Array.isArray(tx.spans)) return;

    tx.spans.filter(filterSpan).forEach((span) => {
      const perfEntry = performance.getEntriesByName(span.name).at(0) as
        | PerformanceResourceTiming
        | undefined;

      /*                */
      if (!perfEntry) return;

      /*                            */
      span.context ??= {};
      span.context.tags ??= {};
      const { tags } = span.context;

      tags.resource_type = getRenderBlockingStatus(perfEntry);
      [tags.resource_team, tags.resource_service, tags.resource_bundle] = parseBundleName(
        perfEntry.name,
      );
      tags.resource_delivery_type = getDeliveryType(perfEntry);
      tags.resource_response_status = perfEntry.responseStatus;
    });
  });

  return payload;
});
